import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlertService, DropdownModule, ModalComponent, ModalModule, StatusModule } from '@michelin/theme';
import { Store } from '@ngxs/store';
import { BehaviorSubject, EMPTY, Observable, Subject, combineLatest, of } from 'rxjs';
import { catchError, debounceTime, finalize, map, switchMap, tap } from 'rxjs/operators';
import { DealerState } from '../../../../../shared/state/DealerState';
import { Dealer } from '../../../../../shared/dealer/models/dealer';
import { CreateWorkOrderService } from '../../../components/createworkorder/create-worker-order.service';
import { TranslationService } from '../../../../../shared/translations/services/translation.service';
import {
  Plant,
  ProcessCode,
  ProductionCustomer,
  TireMake,
  TireSpecification,
  WorkOrderLine as WorkOrderLineInterface
} from '../../../components/createworkorder/models/work-order.interfaces';
import { WorkOrderSettings } from '../../../components/createworkorder/models/work-order-settings.model';
import { optionalBrandNumber, optionalInteger, optionalPattern, sanitizeBrandNumber } from '../../util/work-order-validators';
import { CamRequest } from '../../../../asset-management/models/request/CamRequest';
import { MichelinDropdownAccessibilityDirective } from '../../../../../shared/directives/michelin-dropdown-accessibility.directive';
import { MichelinDropdownOverlayStateService } from '../../../../../shared/directives/michelin-dropdown-overlay-state.service';

@Component({
  selector: 'app-bulk-wo-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatTooltipModule,
    DropdownModule,
    MichelinDropdownAccessibilityDirective,
    ModalModule,
    StatusModule
  ],
  templateUrl: './bulk-wo-modal.component.html',
  styleUrl: './bulk-wo-modal.component.scss'
})
export class BulkWoModalComponent implements OnInit {
  @ViewChild('modalRef') modalRef!: ModalComponent;
  @ViewChild('modalContent') modalContent!: TemplateRef<any>;

  // -------- Inputs --------
  @Input() plant: Plant | null = null;
  @Input() selectedCustomerUuid = '';
  @Input() headerSettings: WorkOrderSettings | null = null;
  @Input() existingLines: WorkOrderLineInterface[] = [];
  @Input() productionCustomers: ProductionCustomer[] = [];
  @Input() preferredTreadSpecPk: string | null = null;

  // -------- Outputs --------
  /** Emits the set of new/updated lines. In edit mode the lines carry the original bulkId so the
   * parent can identify and replace the old group. */
  @Output() linesAdded = new EventEmitter<WorkOrderLineInterface[]>();

  // -------- Injected services --------
  private readonly store = inject(Store);
  private readonly alertService = inject(AlertService);
  private readonly createWorkOrderService = inject(CreateWorkOrderService);
  public readonly translationService = inject(TranslationService);
  private readonly michelinDropdownOverlayState = inject(MichelinDropdownOverlayStateService);

  // -------- Dealer --------
  private dealer!: Dealer;

  // -------- Modal state --------
  isEditMode = false;
  private editingLine: WorkOrderLineInterface | null = null;
  submittedBulk = false;
  bulkErrors: string[] = [];

  // -------- Dropdowns --------
  processCodes: ProcessCode[] = [];
  makes: TireMake[] = [];
  series: TireSpecification[] = [];
  sizes: TireSpecification[] = [];
  treads: TireSpecification[] = [];

  // -------- Loading flags --------
  processCodesLoading = false;
  makesLoading = false;
  seriesLoading = false;
  sizesLoading = false;
  treadsLoading = false;

  // -------- CAM validation --------
  camNtrError: string | null = null;
  camMdotError: string | null = null;
  ntrCamValidating = false;
  mdotCamValidating = false;

  // -------- Visual state --------
  showMoldRadio = false;
  barcodeRequired = false;

  // -------- Barcode preview/validation --------
  computedBarcodes: string[] = [];
  barcodeValidationErrors: string[] = [];
  barcodeValidationLoading = false;
  /** True from the moment the user types in firstBarcode until validation fully completes. */
  barcodeValidationPending = false;
  duplicateBarcodeCount = 0;

  private readonly barcodeInputSubject = new BehaviorSubject<string>('');
  private readonly numberOfLinesSubject = new BehaviorSubject<number>(1);
  private readonly ntrChangeSubject = new Subject<string>();
  private readonly mfgDotChangeSubject = new Subject<string>();

  // -------- Form --------
  bulkWOForm: FormGroup = new FormGroup({
    firstBarcode: new FormControl('', [Validators.maxLength(10), optionalPattern(/^[A-Za-z0-9]+$/, 'barcode')]),
    productionCustomerId: new FormControl('', [Validators.required]),
    numberOfLines: new FormControl(1, [Validators.required, optionalInteger(1, 999)]),
    processCodeId: new FormControl('', [Validators.required]),
    makeId: new FormControl('', Validators.maxLength(30)),
    seriesId: new FormControl('', Validators.maxLength(30)),
    sizeId: new FormControl('', Validators.maxLength(30)),
    treadId: new FormControl('', Validators.maxLength(30)),
    moldType: new FormControl('', Validators.maxLength(30)),
    brandNumber: new FormControl('', [Validators.maxLength(25), optionalBrandNumber()]),
    casingNotes: new FormControl('', [Validators.maxLength(70)]),
    ntrId: new FormControl('', [Validators.maxLength(1), optionalPattern(/^[0-9]{1}$/, 'ntrDigit')]),
    mfgDot: new FormControl('', [Validators.maxLength(4), optionalPattern(/^[A-Za-z0-9]*$/, 'alnum')])
  });

  // Derived work type for the currently selected process code in the bulk modal
  private bulkWorkType: string | null = null;

  // -------- Lifecycle --------
  onBrandNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = sanitizeBrandNumber(input.value);
    input.value = value;
    this.bulkWOForm.get('brandNumber')?.setValue(value, { emitEvent: false });
  }

  ngOnInit(): void {
    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.loadMakes();
    this.setupFormValueChanges();
    this.setupBarcodePreview();
    this.setupDebouncedValidators();
  }

  // -------- Public API --------

  /** Open in create mode. */
  open(): void {
    this.isEditMode = false;
    this.editingLine = null;
    this.submittedBulk = false;
    this.bulkErrors = [];
    this.resetBulkModalState();
    this.applyPlantSettings();
    // Fire-and-forget: process codes load in background; form is blank so ordering doesn't matter
    this.loadProcessCodes().subscribe();
    this.autoSelectMatchingProductionCustomer();
    const addDialogRef = this.modalRef.open({ id: 'add-bulk-wo', width: 1000, title: '', content: this.modalContent });
    addDialogRef.disableClose = true;
    addDialogRef.keydownEvents().subscribe((e) => {
      // Ignore Escape while a Michelin dropdown panel is open — that keypress is meant to
      // close the dropdown only (Michelin/Material don't stop it from bubbling up to us).
      if (e.key === 'Escape' && !this.hasOpenMichelinDropdownPanel()) addDialogRef.close();
    });
  }

  /** Open in edit mode for a bulk group member. */
  openForEdit(item: WorkOrderLineInterface): void {
    this.isEditMode = true;
    this.editingLine = item;
    this.submittedBulk = false;
    this.bulkErrors = [];
    this.resetBulkModalState();
    this.applyPlantSettings();
    // Wait for process codes to load before patching the form: ensures validity checks in
    // patchFormForEdit use the freshly loaded codes for the current plant (not stale codes
    // from a previous open() call against a different plant).
    this.loadProcessCodes().subscribe(() => {
      this.patchFormForEdit(item);
    });
    const editDialogRef = this.modalRef.open({ id: 'edit-bulk-wo', width: 1000, title: '', content: this.modalContent });
    editDialogRef.disableClose = true;
    editDialogRef.keydownEvents().subscribe((e) => {
      // Ignore Escape while a Michelin dropdown panel is open — that keypress is meant to
      // close the dropdown only (Michelin/Material don't stop it from bubbling up to us).
      if (e.key === 'Escape' && !this.hasOpenMichelinDropdownPanel()) editDialogRef.close();
    });
  }

  /**
   * Checks LIVE DOM state (rather than a cached flag) for whether a Michelin/Material select
   * panel is currently open anywhere. CDK overlay panes render as siblings under a global overlay
   * container, not nested inside this modal's DOM, so keydown propagation/dispatch order between
   * the panel and the dialog isn't reliable — a direct DOM check is the only deterministic signal.
   */
  private hasOpenMichelinDropdownPanel(): boolean {
    return !!this.michelinDropdownOverlayState.hasOpenDropdown || !!document.querySelector('.mat-mdc-select-panel');
  }

  // -------- Getters --------

  get bulkProcessCodes(): ProcessCode[] {
    return this.processCodes.filter((pc) => pc.code !== 'SC');
  }

  get computedBarcodesTooltip(): string {
    if (!this.computedBarcodes || this.computedBarcodes.length === 0) return '';
    return this.computedBarcodes.join(', ');
  }

  compareById = (a: any, b: any) => a && b && a.id === b.id;

  /**
   * Disables the Add/Update button while any async validation is in flight, while a
   * prior async run surfaced an unresolved error, or while the form has sync errors.
   * Covers the debounce window (300 ms – 1500 ms) during which the user has typed a
   * value but the async check has not started yet — buttons stay enabled only once
   * the form is clean and all checks have completed.
   */
  get isAddDisabled(): boolean {
    return (
      !this.bulkWOForm.valid || this.barcodeValidationLoading || this.barcodeValidationPending || this.barcodeValidationErrors.length > 0
    );
  }

  // -------- Modal actions --------

  addBulkWOLine(): void {
    this.submittedBulk = true;
    this.bulkWOForm.markAllAsTouched();
    this.bulkErrors = [];

    if (!this.bulkWOForm.valid) {
      return;
    }

    const bulkData = this.bulkWOForm.value;
    const numberOfLines = parseInt(bulkData.numberOfLines) || 1;

    const bulkId = this.isEditMode && this.editingLine?.bulkId ? this.editingLine.bulkId : this.generateUniqueId();

    // Determine base line number
    let baseLineNumber: number;
    if (this.isEditMode && this.editingLine?.bulkId) {
      const bulkGroup = this.existingLines
        .filter((l) => l.bulkId === this.editingLine?.bulkId)
        .sort((a, b) => parseInt(a.lineNumber || '1') - parseInt(b.lineNumber || '1'));
      baseLineNumber = bulkGroup.length > 0 ? parseInt(bulkGroup[0].lineNumber || '1') : this.existingLines.length + 1;
    } else {
      baseLineNumber = this.existingLines.length + 1;
    }

    const newLines: WorkOrderLineInterface[] = [];
    for (let i = 0; i < numberOfLines; i++) {
      newLines.push(this.createWorkOrderLineFromBulkData(bulkData, i, bulkId, numberOfLines, baseLineNumber));
    }

    this.linesAdded.emit(newLines);
    this.resetBulkModalState();
    this.modalRef.dialog?.closeAll();
  }

  /** Blocks characters that browsers allow in type="number" but are invalid here (e, E, +, -, .),
   *  blocks a LEADING zero so the value can never be 0/00/01, and caps the length at 3 digits.
   *  A non-leading 0 is allowed so 10, 20, 100, 200 can be entered.
   *  NOTE: type="number" inputs do NOT expose selectionStart/selectionEnd (they return null),
   *  so leading-zero detection is based on the current value being empty, not caret position. */
  onNumberOfLinesKeydown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    // Reject scientific-notation / sign / decimal keys type="number" otherwise accepts.
    if (['e', 'E', '+', '-', '.'].includes(event.key)) {
      event.preventDefault();
      return;
    }

    const isDigit = event.key >= '0' && event.key <= '9';
    if (!isDigit) return; // allow Backspace, Delete, arrows, Tab, Home/End, etc.

    const value = input.value ?? '';

    // Block '0' only when the field is currently empty — that first 0 would make the value
    // 0 (a leading zero). Once at least one digit exists, allow 0 so 10, 20, 100, 200 work.
    if (event.key === '0' && value.length === 0) {
      event.preventDefault();
      return;
    }

    // Enforce max 3 digits (max 999).
    if (value.length >= 3) {
      event.preventDefault();
    }
  }

  onFirstBarcodeBlur(): void {
    const firstBarcode = (this.bulkWOForm.get('firstBarcode')?.value || '') as string;
    this.barcodeInputSubject.next(firstBarcode);
  }

  onCancelBulk(): void {
    this.series = [];
    this.sizes = [];
    this.treads = [];
    this.isEditMode = false;
    this.editingLine = null;
    this.modalRef.dialog?.closeAll();
  }

  onModalClosed(): void {
    this.series = [];
    this.sizes = [];
    this.treads = [];
    this.isEditMode = false;
    this.editingLine = null;
  }

  // -------- Private helpers --------

  private applyPlantSettings(): void {
    // Show mold radio only for hybrid plants
    this.showMoldRadio = this.plant?.isHybridPlant === true;
    // Barcode required unless plant explicitly says 'N'
    this.barcodeRequired = this.plant?.reqBarcodeEntry?.toUpperCase() !== 'N';
    this.updateBarcodeValidation();
    this.updateMoldRadioControl();
  }

  private updateBarcodeValidation(): void {
    const firstBarcodeControl = this.bulkWOForm.get('firstBarcode');
    if (!firstBarcodeControl) return;
    if (this.barcodeRequired) {
      firstBarcodeControl.setValidators([Validators.required, Validators.maxLength(10), optionalPattern(/^[A-Za-z0-9]+$/, 'barcode')]);
    } else {
      firstBarcodeControl.setValidators([Validators.maxLength(10), optionalPattern(/^[A-Za-z0-9]+$/, 'barcode')]);
    }
    firstBarcodeControl.updateValueAndValidity({ emitEvent: false });
  }

  private updateMoldRadioControl(): void {
    const moldCtrl = this.bulkWOForm.get('moldType');
    if (!moldCtrl) return;
    if (this.showMoldRadio) {
      if (moldCtrl.disabled) moldCtrl.enable({ emitEvent: false });
    } else {
      moldCtrl.setValue('', { emitEvent: false });
      moldCtrl.disable({ emitEvent: false });
    }
  }

  private loadMakes(): void {
    if (!this.dealer?.id) return;
    this.makesLoading = true;
    this.createWorkOrderService.getMakes(this.dealer.id).subscribe({
      next: (data) => {
        this.makes = data;
        this.makesLoading = false;
      },
      error: () => {
        this.makesLoading = false;
      }
    });
  }

  private loadProcessCodes(): Observable<ProcessCode[]> {
    if (!this.dealer?.id) {
      return of([]);
    }
    const plantId = this.plant?.id;
    this.processCodesLoading = true;
    return this.createWorkOrderService.getProcessCodes(this.dealer.id, plantId).pipe(
      tap((data) => {
        this.processCodes = data;
        this.processCodesLoading = false;
      }),
      catchError(() => {
        this.processCodesLoading = false;
        return of([]);
      })
    );
  }

  private autoSelectMatchingProductionCustomer(): void {
    if (!this.selectedCustomerUuid || this.productionCustomers.length === 0) return;
    const match = this.productionCustomers.find((c) => c.id === this.selectedCustomerUuid);
    if (match) {
      this.bulkWOForm.patchValue({ productionCustomerId: match }, { emitEvent: false });
    }
  }

  resetBulkModalState(): void {
    this.bulkWOForm.reset({ numberOfLines: 1 }, { emitEvent: false });
    this.barcodeInputSubject.next('');
    this.numberOfLinesSubject.next(1);
    this.series = [];
    this.sizes = [];
    this.treads = [];
    this.computedBarcodes = [];
    this.barcodeValidationErrors = [];
    this.barcodeValidationLoading = false;
    this.barcodeValidationPending = false;
    this.duplicateBarcodeCount = 0;
    this.submittedBulk = false;
    this.bulkErrors = [];
    this.camNtrError = null;
    this.camMdotError = null;
    // Re-enable cascading controls after reset
    ['seriesId', 'sizeId', 'treadId'].forEach((ctrl) => {
      const c = this.bulkWOForm.get(ctrl);
      if (c?.disabled) c.enable({ emitEvent: false });
    });
    this.updateMoldRadioControl();
  }

  private setupFormValueChanges(): void {
    this.bulkWOForm.get('makeId')?.valueChanges.subscribe((makeVal) => {
      if (makeVal) {
        this.onMakeChange(makeVal);
        this.updateBulkControlsForMake(makeVal);
      } else {
        this.bulkWOForm.patchValue({ seriesId: '', sizeId: '', treadId: '' }, { emitEvent: false });
        this.updateBulkControlsForMake(null);
      }
    });

    this.bulkWOForm.get('seriesId')?.valueChanges.subscribe((seriesVal) => {
      if (seriesVal) {
        this.onSeriesChange(seriesVal);
      } else {
        this.bulkWOForm.patchValue({ sizeId: '', treadId: '' }, { emitEvent: false });
      }
    });

    this.bulkWOForm.get('sizeId')?.valueChanges.subscribe((sizeVal) => {
      if (sizeVal) {
        this.onSizeChange(sizeVal);
      } else {
        this.bulkWOForm.patchValue({ treadId: '' }, { emitEvent: false });
      }
    });

    this.bulkWOForm.get('processCodeId')?.valueChanges.subscribe((pcVal) => {
      const code = typeof pcVal === 'object' ? pcVal?.code || pcVal?.value || '' : pcVal || '';
      const specialCodes = ['IO', 'NP', 'RO', 'SC', 'SO'];
      const isSpecial = specialCodes.includes(String(code).toUpperCase());
      const treadCtrl = this.bulkWOForm.get('treadId');
      const moldCtrl = this.bulkWOForm.get('moldType');
      if (isSpecial) {
        treadCtrl?.disable({ emitEvent: false });
        treadCtrl?.setValue('', { emitEvent: false });
        moldCtrl?.setValue('', { emitEvent: false });
        moldCtrl?.disable({ emitEvent: false });
      } else {
        treadCtrl?.enable({ emitEvent: false });
        if (this.showMoldRadio) moldCtrl?.enable({ emitEvent: false });
        const sizeVal = this.bulkWOForm.get('sizeId')?.value;
        if (sizeVal) {
          this.onSizeChange(sizeVal);
        } else {
          this.bulkWOForm.patchValue({ treadId: '' }, { emitEvent: false });
        }
      }

      // Fetch derived work type for selected process code (best-effort). Fall back to client-side mapping.
      const pcCode = (code || '').toString();
      this.bulkWorkType = null;
      if (pcCode) {
        if (this.dealer?.id) {
          this.createWorkOrderService.getWorkType(this.dealer.id, pcCode).subscribe({
            next: (resp) => {
              this.bulkWorkType = resp?.workType ?? this.clientSideDeriveWorkType(pcCode);
            },
            error: () => {
              this.bulkWorkType = this.clientSideDeriveWorkType(pcCode);
            }
          });
        } else {
          this.bulkWorkType = this.clientSideDeriveWorkType(pcCode);
        }
      }
    });

    this.bulkWOForm.get('moldType')?.valueChanges.subscribe(() => {
      this.bulkWOForm.patchValue({ treadId: '' }, { emitEvent: false });
      const sizeVal = this.bulkWOForm.get('sizeId')?.value;
      if (sizeVal) this.onSizeChange(sizeVal);
    });

    this.bulkWOForm.get('productionCustomerId')?.valueChanges.subscribe(() => {
      // customerId is part of CAM spec request — re-validate NTR and mfgDot when customer changes
      const ntrVal = this.bulkWOForm.get('ntrId')?.value;
      const mfgDotVal = this.bulkWOForm.get('mfgDot')?.value;
      if (ntrVal) this.checkNtrCamViolation(ntrVal);
      if (mfgDotVal) this.checkMfgDotCamViolation(mfgDotVal);
    });
  }

  private setupBarcodePreview(): void {
    // Set pending flag immediately (before debounce) so the button stays disabled
    // for the entire window between typing and validation result.
    this.bulkWOForm.get('firstBarcode')?.valueChanges.subscribe((val) => {
      if (val) this.barcodeValidationPending = true;
    });

    this.bulkWOForm
      .get('numberOfLines')
      ?.valueChanges.pipe(debounceTime(300))
      .subscribe((val) => {
        const ctrl = this.bulkWOForm.get('numberOfLines');
        // Only forward a valid count — invalid input (negative, >999, decimal, NaN) suppresses barcode validation
        const n = ctrl?.valid ? parseInt(val) || 0 : 0;
        if (this.bulkWOForm.get('firstBarcode')?.value) this.barcodeValidationPending = true;
        this.numberOfLinesSubject.next(n);
      });

    combineLatest([this.barcodeInputSubject, this.numberOfLinesSubject])
      .pipe(
        debounceTime(500),
        switchMap(([barcode, count]) => this.validateComputedBarcodes(barcode, count))
      )
      .subscribe();
  }

  private setupDebouncedValidators(): void {
    this.bulkWOForm
      .get('ntrId')
      ?.valueChanges.pipe(debounceTime(300))
      .subscribe((val) => {
        this.ntrChangeSubject.next(val || '');
      });
    this.ntrChangeSubject.subscribe((val) => this.checkNtrCamViolation(val));

    this.bulkWOForm
      .get('mfgDot')
      ?.valueChanges.pipe(debounceTime(300))
      .subscribe((val) => {
        this.mfgDotChangeSubject.next(val || '');
      });
    this.mfgDotChangeSubject.subscribe((val) => this.checkMfgDotCamViolation(val));
  }

  private checkNtrCamViolation(ntrValue: string): void {
    const ntrNum = ntrValue ? parseInt(ntrValue, 10) : 0;
    const checkCamWoEntry = this.plant?.ckCamSpecWo === 'Y';
    if (!checkCamWoEntry || !ntrValue || isNaN(ntrNum) || ntrNum === 0) {
      this.camNtrError = null;
      return;
    }
    const camReq = this.buildCamRequest({ ntr: ntrNum, mdot: this.bulkWOForm.get('mfgDot')?.value || null });
    this.ntrCamValidating = true;
    this.createWorkOrderService.getMatchingCamSpec(this.dealer.id, camReq).subscribe({
      next: (response) => {
        this.ntrCamValidating = false;
        const treadVal = this.bulkWOForm.get('treadId')?.value;
        const treadId = typeof treadVal === 'object' ? treadVal?.id : treadVal;
        const selectedTread = treadId ? this.treads.find((t) => t.id === treadId) : null;
        const m3Flag = selectedTread?.m3Flag === 'Y';
        const violation = this.createWorkOrderService.checkv4NtrCamSpecs(
          {
            ntrId: this.bulkWOForm.get('ntrId')?.value,
            workType: '',
            ignoreCheckCamSpecNtr: '',
            application: null
          },
          response,
          m3Flag
        );
        if (violation?.ntrInError) {
          this.camNtrError = `NTR value ${violation.value} exceeds the CAM Spec limit of ${violation.limit}`;
        } else {
          this.camNtrError = null;
        }
      },
      error: () => {
        this.ntrCamValidating = false;
      }
    });
  }

  private checkMfgDotCamViolation(mfgDotValue: string): void {
    const mfgDot = (mfgDotValue || '').trim();
    const mfgDotCtrl = this.bulkWOForm.get('mfgDot');
    if (mfgDot && this.headerSettings && this.createWorkOrderService.validateMfgDotOnly(mfgDot, this.headerSettings)) {
      if (mfgDotCtrl?.hasError('mfgDotFormat')) {
        const { ...rem } = mfgDotCtrl.errors as Record<string, any>;
        mfgDotCtrl.setErrors(Object.keys(rem).length ? rem : null);
      }
      const checkCamWoEntry = this.plant?.ckCamSpecWo === 'Y';
      if (!checkCamWoEntry) {
        this.camMdotError = null;
        return;
      }
      const camReq = this.buildCamRequest({ ntr: this.bulkWOForm.get('ntrId')?.value || null, mdot: mfgDot });
      this.mdotCamValidating = true;
      this.createWorkOrderService.getMatchingCamSpec(this.dealer.id, camReq).subscribe({
        next: (response) => {
          this.mdotCamValidating = false;
          // const treadVal = this.bulkWOForm.get('treadId')?.value;
          // const treadId = typeof treadVal === 'object' ? treadVal?.id : treadVal;
          // const selectedTread = treadId ? this.treads.find((t) => t.id === treadId) : null;
          // const m3Flag = selectedTread?.m3Flag === 'Y';
          const violation = this.createWorkOrderService.checkV4MdotCamSpecs(
            {
              mfgDot: this.bulkWOForm.get('mfgDot')?.value || '',
              application: null,
              ignoreCheckCamSpecAge: ''
            },
            response,
            this.headerSettings!
          );
          if (violation?.ageInError) {
            this.camMdotError = `MDOT age ${violation.value} months exceeds the CAM Spec limit of ${violation.limit} months`;
          } else {
            this.camMdotError = null;
          }
        },
        error: () => {
          this.mdotCamValidating = false;
        }
      });
      return;
    }
    if (mfgDot || this.headerSettings?.isMfgDotFieldRequired) {
      const useWeeks = ['SA', 'NA'].includes(this.headerSettings?.zone?.toUpperCase() || '') || this.headerSettings?.adjCalMethod === '3';
      const hint = useWeeks
        ? 'Expected WWYY format (e.g. 1226 = week 12 of 2026) and it should not be in the future'
        : 'Expected MMyy format (e.g. 0326 = March 2026) and it should not be in the future';
      const existing = mfgDotCtrl?.errors ? { ...mfgDotCtrl.errors } : {};
      mfgDotCtrl?.setErrors({ ...existing, mfgDotFormat: { hint } });
      this.camMdotError = null;
      return;
    }
    this.camMdotError = null;
    if (mfgDotCtrl?.hasError('mfgDotFormat')) {
      const { ...rem } = mfgDotCtrl.errors as Record<string, any>;
      mfgDotCtrl.setErrors(Object.keys(rem).length ? rem : null);
    }
  }

  private buildCamRequest(overrides: { ntr: number | null; mdot: string | null }): CamRequest {
    const makeVal = this.bulkWOForm.get('makeId')?.value;
    const seriesVal = this.bulkWOForm.get('seriesId')?.value;
    const sizeVal = this.bulkWOForm.get('sizeId')?.value;
    const treadVal = this.bulkWOForm.get('treadId')?.value;
    const prodCustVal = this.bulkWOForm.get('productionCustomerId')?.value;
    const treadId = typeof treadVal === 'object' ? treadVal?.id : treadVal;
    const selectedTread = treadId ? this.treads.find((t) => t.id === treadId) : null;
    const m3Flag = selectedTread?.m3Flag === 'Y';
    return {
      make: (typeof makeVal === 'object' ? makeVal?.name : null) || null,
      series: (typeof seriesVal === 'object' ? seriesVal?.value : null) || null,
      size: (typeof sizeVal === 'object' ? sizeVal?.value : null) || null,
      tread: (typeof treadVal === 'object' ? treadVal?.value : null) || null,
      workType: '',
      application: null,
      axle: null,
      customerId: this.extractId(prodCustVal) || '',
      customerCode: (typeof prodCustVal === 'object' ? prodCustVal?.code : null) || null,
      ntr: overrides.ntr,
      mdot: overrides.mdot,
      m3Flag,
      ignoreCheckCamSpecNtr: false,
      ignoreCheckCamSpecAge: false
    };
  }

  // -------- Cascade dropdown handlers --------

  private onMakeChange(makeVal: any): void {
    const makeId = this.extractId(makeVal);
    if (!makeId || !this.dealer?.id) return;
    this.bulkWOForm.patchValue({ seriesId: '', sizeId: '', treadId: '' }, { emitEvent: false });
    this.seriesLoading = true;
    this.createWorkOrderService.getSeries(this.dealer.id, makeId).subscribe({
      next: (data) => {
        this.series = data;
        this.seriesLoading = false;
        this.sizes = [];
        this.treads = [];
      },
      error: () => {
        this.seriesLoading = false;
      }
    });
    this.sizes = [];
    this.treads = [];
  }

  private onSeriesChange(seriesVal: any): void {
    const seriesId = this.extractId(seriesVal);
    const makeVal = this.bulkWOForm.get('makeId')?.value;
    const makeId = this.extractId(makeVal);
    if (!seriesId || !makeId || !this.dealer?.id) return;
    this.bulkWOForm.patchValue({ sizeId: '', treadId: '' }, { emitEvent: false });
    const plantId = this.plant?.id;
    this.sizesLoading = true;
    this.createWorkOrderService.getSizes(this.dealer.id, makeId, seriesId, plantId).subscribe({
      next: (data) => {
        this.sizes = data;
        this.sizesLoading = false;
        this.treads = [];
      },
      error: () => {
        this.sizesLoading = false;
      }
    });
    this.treads = [];
  }

  private onSizeChange(sizeVal: any): void {
    const sizeId = this.extractId(sizeVal);
    const plantId = this.plant?.id;
    if (!sizeId || !plantId || !this.dealer?.id) return;
    this.bulkWOForm.patchValue({ treadId: '' }, { emitEvent: false });
    if (this.showMoldRadio) {
      const moldCtrl = this.bulkWOForm.get('moldType');
      if (!moldCtrl?.enabled || !moldCtrl?.value) {
        this.treads = [];
        return;
      }
    }
    const makeId = this.extractId(this.bulkWOForm.get('makeId')?.value) || undefined;
    const seriesId = this.extractId(this.bulkWOForm.get('seriesId')?.value) || undefined;
    const moldType = this.bulkWOForm.get('moldType')?.value || undefined;
    if (!this.isSpecialPc()) {
      this.treadsLoading = true;
      this.createWorkOrderService
        .getTreads(this.dealer.id, sizeId, plantId, this.preferredTreadSpecPk ?? undefined, makeId, seriesId, moldType)
        .subscribe({
          next: (data) => {
            this.treads = data;
            this.treadsLoading = false;
          },
          error: () => {
            this.treadsLoading = false;
          }
        });
    }
  }

  private updateBulkControlsForMake(makeVal: any): void {
    const makeId = this.extractId(makeVal);
    let selectedMake: any = null;
    if (makeId) selectedMake = this.makes.find((m) => m.id === makeId) || null;
    else if (makeVal && typeof makeVal === 'object') selectedMake = makeVal;

    const isInvalid = selectedMake ? selectedMake.isValid === 'N' || selectedMake.isValid === 'n' : false;
    const seriesCtrl = this.bulkWOForm.get('seriesId');
    const sizeCtrl = this.bulkWOForm.get('sizeId');
    const treadCtrl = this.bulkWOForm.get('treadId');
    const moldCtrl = this.bulkWOForm.get('moldType');

    if (isInvalid) {
      this.bulkWOForm.patchValue({ seriesId: '', sizeId: '', treadId: '' }, { emitEvent: false });
      seriesCtrl?.disable({ emitEvent: false });
      sizeCtrl?.disable({ emitEvent: false });
      treadCtrl?.disable({ emitEvent: false });
      if (this.showMoldRadio && moldCtrl) {
        moldCtrl.setValue('', { emitEvent: false });
        moldCtrl.disable({ emitEvent: false });
      }
    } else {
      seriesCtrl?.enable({ emitEvent: false });
      sizeCtrl?.enable({ emitEvent: false });
      if (!this.isSpecialPc()) {
        treadCtrl?.enable({ emitEvent: false });
      } else {
        treadCtrl?.disable({ emitEvent: false });
      }
      if (moldCtrl && this.showMoldRadio && !this.isSpecialPc()) moldCtrl.enable({ emitEvent: false });
    }
  }

  // -------- Edit mode helpers --------

  private patchFormForEdit(item: WorkOrderLineInterface): void {
    const makeId = this.extractId(item.makeId);
    const seriesId = this.extractId(item.seriesId);
    const sizeId = this.extractId(item.sizeId);
    const plantId = this.plant?.id;
    const processCodeId = this.extractId(item.processCodeId);
    // Process codes are plant-scoped: only accept the stored code if it exists in the
    // freshly loaded list for the current plant. A null result means the plant changed
    // and the old code is no longer valid.
    const processCodeObj = this.processCodes.find((p) => p.id === processCodeId) ?? null;
    const prodCustId = this.extractId(item.productionCustomerId);
    // Production customers are customer-scoped: only accept the stored value if it still
    // exists in the list bound to the current header customer selection.
    const prodCustObj = this.productionCustomers.find((c) => c.id === prodCustId) ?? null;

    // Collect contextual warnings for fields that could not be restored automatically.
    // These are shown as a banner so the user knows which fields need attention before saving.
    const editWarnings: string[] = [];
    if (processCodeId && !processCodeObj) {
      editWarnings.push('The process code from this entry is not available for the current plant. Please select a valid process code.');
    }
    if (prodCustId && !prodCustObj) {
      editWarnings.push('The production customer from this entry is no longer available. Please select a production customer.');
    }
    if (editWarnings.length > 0) {
      this.bulkErrors = editWarnings;
    }
    const makeObj = this.makes.find((m) => m.id === makeId) || item.makeId || '';

    const bulkGroupLines = this.existingLines
      .filter((l) => l.bulkId && l.bulkId === item.bulkId)
      .sort((a, b) => parseInt(a.lineNumber || '0') - parseInt(b.lineNumber || '0'));
    const firstLine = bulkGroupLines.length > 0 ? bulkGroupLines[0] : item;

    this.bulkWOForm.patchValue(
      {
        firstBarcode: firstLine.barcode || '',
        numberOfLines: item.totalLines ?? bulkGroupLines.length,
        processCodeId: processCodeObj ?? '',
        productionCustomerId: prodCustObj ?? '',
        makeId: makeObj,
        moldType: item.moldType || '',
        mfgDot: item.mfgDot || '',
        brandNumber: item.brandNumber || '',
        casingNotes: item.casingNotes || '',
        // Prefer the first line's NTR when restoring a bulk group, fallback to the item
        ntrId: firstLine.ntrId || item.ntrId || ''
      },
      { emitEvent: false }
    );

    // Preserve or pre-set the derived work type so createWorkOrderLineFromBulkData can use it
    this.bulkWorkType = item.workType || null;

    this.updateBulkControlsForMake(makeObj);

    if (makeId && this.dealer?.id) {
      this.seriesLoading = true;
      this.createWorkOrderService.getSeries(this.dealer.id, makeId).subscribe({
        next: (data) => {
          this.series = data;
          this.seriesLoading = false;
          const seriesObj = data.find((s) => s.id === seriesId);
          if (seriesObj) this.bulkWOForm.patchValue({ seriesId: seriesObj }, { emitEvent: false });
        },
        error: () => {
          this.seriesLoading = false;
        }
      });

      if (seriesId) {
        this.sizesLoading = true;
        this.createWorkOrderService.getSizes(this.dealer.id, makeId, seriesId, plantId).subscribe({
          next: (data) => {
            this.sizes = data;
            this.sizesLoading = false;
            const sizeObj = data.find((s) => s.id === sizeId);
            if (sizeObj) this.bulkWOForm.patchValue({ sizeId: sizeObj }, { emitEvent: false });
          },
          error: () => {
            this.sizesLoading = false;
          }
        });

        if (sizeId && plantId) {
          this.treadsLoading = true;
          this.createWorkOrderService
            .getTreads(
              this.dealer.id,
              sizeId,
              plantId,
              this.preferredTreadSpecPk ?? undefined,
              makeId || undefined,
              seriesId || undefined,
              item.moldType || undefined
            )
            .subscribe({
              next: (data) => {
                this.treads = data;
                this.treadsLoading = false;
                const treadId = this.extractId(item.treadId);
                const treadObj = data.find((t) => t.id === treadId);
                if (treadObj) this.bulkWOForm.patchValue({ treadId: treadObj }, { emitEvent: false });
              },
              error: () => {
                this.treadsLoading = false;
              }
            });
        }
      }
    }
  }

  // -------- Barcode validation --------

  private validateComputedBarcodes(firstBarcode: string, numberOfLines: number): Observable<void> {
    this.barcodeValidationErrors = [];
    this.computedBarcodes = [];
    if (!firstBarcode || numberOfLines <= 0) {
      this.barcodeValidationPending = false;
      return EMPTY;
    }
    const barcodes: string[] = [];
    for (let i = 0; i < numberOfLines; i++) {
      barcodes.push(this.generateBarcodeFromFirstBarcode(firstBarcode, i));
    }
    this.computedBarcodes = barcodes;
    this.duplicateBarcodeCount = 0;

    const oversized = barcodes.filter((b) => b && b.length > 10);
    if (oversized.length > 0) {
      this.barcodeValidationErrors = [`${oversized.length} barcode(s) exceed maximum length of 10 characters`];
      const ctrl = this.bulkWOForm.get('firstBarcode');
      const existing = ctrl?.errors ? { ...ctrl.errors } : {};
      ctrl?.setErrors({ ...existing, barcodeOversize: true });
      this.barcodeValidationPending = false;
      return EMPTY;
    }

    // Check for duplicates against already-added lines in the current work order (in-memory)
    const editBulkId = this.isEditMode && this.editingLine?.bulkId ? this.editingLine.bulkId : null;
    const existingBarcodeSet = new Set(
      this.existingLines.filter((l) => !editBulkId || l.bulkId !== editBulkId).map((l) => (l.barcode || '').toUpperCase())
    );
    const localDuplicates = barcodes.filter((b) => b && existingBarcodeSet.has(b.toUpperCase()));
    if (localDuplicates.length > 0) {
      this.barcodeValidationErrors = [`${localDuplicates.length} barcode(s) already added to this work order`];
      this.duplicateBarcodeCount = localDuplicates.length;
      const ctrl = this.bulkWOForm.get('firstBarcode');
      const existing = ctrl?.errors ? { ...ctrl.errors } : {};
      ctrl?.setErrors({ ...existing, barcodeExistsLocal: true });
      this.barcodeValidationPending = false;
      return EMPTY;
    }
    // Clear any stale local-duplicate error from a prior check
    const ctrl0 = this.bulkWOForm.get('firstBarcode');
    if (ctrl0?.hasError('barcodeExistsLocal')) {
      const errors = { ...ctrl0.errors };
      delete errors['barcodeExistsLocal'];
      ctrl0.setErrors(Object.keys(errors).length > 0 ? errors : null);
    }

    if (!this.dealer?.id) {
      this.barcodeValidationPending = false;
      return EMPTY;
    }

    this.barcodeValidationLoading = true;
    return this.createWorkOrderService.validateBarcodes(this.dealer.id, barcodes).pipe(
      finalize(() => {
        this.barcodeValidationLoading = false;
        this.barcodeValidationPending = false;
      }),
      tap((response) => {
        if (!response.valid) {
          const duplicates = response.duplicateBarcodes || [];
          this.barcodeValidationErrors = response.errorMessages && response.errorMessages.length > 0 ? response.errorMessages : duplicates;
          this.duplicateBarcodeCount = Array.isArray(duplicates) ? duplicates.length : 0;
          const ctrl = this.bulkWOForm.get('firstBarcode');
          const existing = ctrl?.errors ? { ...ctrl.errors } : {};
          ctrl?.setErrors({ ...existing, barcodeExists: true });
        } else {
          this.barcodeValidationErrors = [];
          this.duplicateBarcodeCount = 0;
          const ctrl = this.bulkWOForm.get('firstBarcode');
          if (ctrl?.hasError('barcodeExists')) {
            const errors = { ...ctrl.errors };
            delete errors['barcodeExists'];
            ctrl.setErrors(Object.keys(errors).length > 0 ? errors : null);
          }
        }
      }),
      catchError(() => {
        this.duplicateBarcodeCount = 0;
        return EMPTY;
      }),
      map(() => undefined)
    );
  }

  private generateBarcodeFromFirstBarcode(firstBarcode: string, index: number): string {
    if (!firstBarcode || firstBarcode.trim() === '') return '';

    let processed = firstBarcode.trim();
    if (this.headerSettings?.barcodeLeadingZero) {
      const padded = this.createWorkOrderService.applyLeadingZeroPadding(firstBarcode.trim(), true);
      if (padded !== processed) {
        this.bulkWOForm.patchValue({ firstBarcode: padded }, { emitEvent: false });
        processed = padded;
      }
    }

    let numberAtEnd = '';
    for (let i = processed.length - 1; i >= 0; i--) {
      const c = processed.charAt(i);
      if (c >= '0' && c <= '9') numberAtEnd = c + numberAtEnd;
      else break;
    }

    let prefix = '';
    let seq = 0;
    let padLen = 0;

    if (numberAtEnd !== '') {
      prefix = processed.substring(0, processed.length - numberAtEnd.length);
      seq = parseInt(numberAtEnd, 10);
      padLen = numberAtEnd.length;
    } else {
      prefix = processed;
      seq = 1;
      padLen = 0;
    }

    const newSeq = seq + index;
    const formatted = padLen > 0 ? newSeq.toString().padStart(padLen, '0') : newSeq.toString();
    return (prefix + formatted).toUpperCase();
  }

  private createWorkOrderLineFromBulkData(
    bulkData: any,
    index: number,
    bulkId: string,
    totalLines: number,
    baseLineNumber: number
  ): WorkOrderLineInterface {
    return {
      lineType: 'bulk',
      id: this.generateUniqueId(),
      lineNumber: (baseLineNumber + index).toString(),
      barcode: this.generateBarcodeFromFirstBarcode(bulkData.firstBarcode, index),
      processCodeId: bulkData.processCodeId,
      productionCustomerId: bulkData.productionCustomerId,
      customerName: '',
      makeId: bulkData.makeId,
      seriesId: bulkData.seriesId,
      sizeId: bulkData.sizeId,
      treadId: bulkData.treadId,
      moldType: this.showMoldRadio && this.bulkWOForm.get('moldType')?.enabled && bulkData.moldType ? bulkData.moldType : null,
      mfgDot: bulkData.mfgDot || '',
      brandNumber: bulkData.brandNumber || '',
      casingNotes: bulkData.casingNotes || '',
      bulkId,
      totalLines,
      // Use derived work type from backend if available, otherwise fall back to client-side mapping
      workType:
        (this.bulkWorkType ??
          this.clientSideDeriveWorkType(
            typeof bulkData.processCodeId === 'object'
              ? bulkData.processCodeId?.code || bulkData.processCodeId?.value
              : bulkData.processCodeId
          )) ||
        '',
      casingStatus: '',
      ntrId: bulkData.ntrId || '',
      post: '',
      overrideGrade: '',
      systemGrade: '',
      wheelType: '',
      wheelColor: ''
    } as WorkOrderLineInterface;
  }

  // Client-side fallback mapping that mirrors backend deriveWorkTypeFromProcessCode.
  private clientSideDeriveWorkType(processCode: any): string | null {
    if (!processCode) return null;
    const code = ('' + processCode).trim().toUpperCase();
    if (['BS', 'DS', 'CP', 'CC', 'RR'].includes(code)) return 'RR';
    if (code === 'RO') return 'RO';
    if (code === 'SO') return 'SO';
    if (code === 'IO') return 'IO';
    if (code === 'AD') return 'AD';
    if (code === 'SC') return 'SC';
    if (code === 'NP') return 'NP';
    if (code === 'RS') return 'RS';
    if (code === 'DT') return 'DT';
    return null;
  }

  private generateUniqueId(): string {
    return 'wo-line-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  private extractId(value: any): string {
    if (!value) return '';
    if (typeof value === 'object' && value.id) return value.id;
    return typeof value === 'string' ? value : '';
  }

  private isSpecialPc(): boolean {
    const pcVal = this.bulkWOForm.get('processCodeId')?.value;
    const pcCode = typeof pcVal === 'object' ? pcVal?.code || pcVal?.value || '' : pcVal || '';
    return ['IO', 'SC', 'NP', 'RO', 'SO', 'DT'].includes(String(pcCode).toUpperCase());
  }
}
